import { FC, ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { Divider, Grid, UnstyledButton } from "@mantine/core";
import { useState } from "react";
import { useGetOrderById, postUpdateOrder, postOrderCoordinates, useGetCreateOrderValues } from "@/services/Orders";
import { isSalesOrder } from "@/utils/Orders";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { useAuth } from "@/contexts/AuthContext";
import { Order, CreateOrder, Unit } from "@/types";
import Spinner from "@/components/atoms/Spinner/Spinner";
import Stepper from "@/components/molecules/Stepper";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Heading from "@/components/atoms/Heading";
import Group from "@/components/atoms/Group";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import TextInput from "@/components/atoms/TextInput";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import CTA from "@/components/atoms/CTA";
import ImageCarousel from "@/components/molecules/ImageCarousel";
import CoordinateFields from "@/components/molecules/CoordinateFields";
import styles from "./styles.module.css";
import dayjs from "dayjs";
import { useGetImagesByOrderId } from "@/services/Documents";
import { buildPickupCoordinates, formatCoordinate } from "@/utils/coordinates";
import { validateCoordinatePair } from "@/validation/coordinates";

const OrderDetailsSchema = z
  .object({
    price: z.string().nonempty({ message: "priceRequired" }),
    latitude: z.string().optional(),
    longitude: z.string().optional(),
    capturedAt: z.string().optional().nullable(),
  })
  .superRefine(({ latitude, longitude }, ctx) => {
    validateCoordinatePair(latitude, longitude, ctx, { keyPrefix: "validation:" });
  });

type OrderDetailsFormValues = z.infer<typeof OrderDetailsSchema>;
const CAN_EDIT_COORDINATES = false;

export default function OrderDetails() {
  const { orderId } = useParams<{ orderId: string }>();
  const { user, currentCompany, isViewer } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);

  const orderIdNum = orderId ? Number(orderId) : null;
  const { data: order, isLoading } = useGetOrderById(orderIdNum, undefined, user?.language.id);
  const { data: createOrderValues } = useGetCreateOrderValues();
  const { data: orderImages, isLoading: isLoadingImages } = useGetImagesByOrderId(orderIdNum);

  const isSell = order ? isSalesOrder(order) : false;
  const orderCurrency = order?.orderCurrency;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { dirtyFields },
  } = useForm<OrderDetailsFormValues>({
    resolver: zodResolver(OrderDetailsSchema),
    values: {
      price: order && orderCurrency ? (order.price?.[orderCurrency]?.toString() ?? "") : "",
      latitude: formatCoordinate(order?.pickupCoordinates?.latitude),
      longitude: formatCoordinate(order?.pickupCoordinates?.longitude),
      capturedAt: order?.pickupCoordinates?.capturedAt ?? null,
    },
  });

  const { mutateAsync: mutateUpdateOrder, isPending } = useMutation({
    mutationFn: async (data: OrderDetailsFormValues) => {
      if (!orderIdNum || !order) return;

      const orderCurrency = createOrderValues?.currencies.find(
        (c) => c.value.toUpperCase() === order.orderCurrency?.toUpperCase()
      );
      if (!orderCurrency) return;

      await postUpdateOrder(orderIdNum, convertOrderToCreateOrder(order, orderCurrency, parseFloat(data.price)));

      const coordinatesTouched = CAN_EDIT_COORDINATES && (!!dirtyFields.latitude || !!dirtyFields.longitude);
      if (coordinatesTouched) {
        const coordinates = buildPickupCoordinates(data.latitude, data.longitude, data.capturedAt);
        if (coordinates) await postOrderCoordinates(orderIdNum, coordinates);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getOrderById", orderIdNum],
      });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:orders.orderUpdated"))
      );
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.orderUpdatedError"))
      );
    },
  });

  if (!order) return <Spinner loading={isLoading} />;

  const onSubmit = handleSubmit((data) => mutateUpdateOrder(data));
  const salesItemName =
    user?.language.id && order.salesItem.nameTranslated?.[user.language.id]
      ? order.salesItem.nameTranslated[user.language.id]
      : order.salesItem.salesItem;

  type StepConfig = {
    label: string;
    content: ReactNode;
  };

  const productStep: StepConfig = {
    label: t("common:type"),
    content: (
      <>
        <Grid>
          <Grid.Col span={6}>
            <TextInput label={t("product:productType")} value={salesItemName} disabled />
          </Grid.Col>
          {order.salesItem.salesItemProperties?.map((prop) => {
            const matchingProp = order.properties?.find((p) => p.index === prop.salesItemPropertyId);
            const label =
              prop.salesItemPropertyTranslated?.find((tr) => tr.languageId === user?.language.id)?.propertyTranslated ??
              prop.salesItemPropertyName;
            return (
              <Grid.Col span={6} key={prop.salesItemPropertyId}>
                <TextInput label={label} value={matchingProp?.value ?? "-"} disabled />
              </Grid.Col>
            );
          })}
        </Grid>

        <Divider my="xl" />

        <Grid>
          <Grid.Col span={6}>
            <Group justify="flex-start" align="flex-start" wrap="nowrap" gap={0}>
              <Controller
                control={control}
                name="price"
                render={({ field, fieldState }) => (
                  <TextInput
                    label={t("common:price")}
                    withAsterisk
                    {...field}
                    error={fieldState.error ? t("validation:" + fieldState.error.message) : undefined}
                  />
                )}
              />
              <TextInput label={t("common:currency")} value={orderCurrency ?? ""} disabled />
            </Group>
          </Grid.Col>

          <Grid.Col span={6}>
            <Group justify="flex-start" align="flex-start" wrap="nowrap" gap={0}>
              <TextInput label={t("common:amount")} value={order.amount?.toString() ?? ""} disabled />
              <TextInput label="+/-" value={order.amountDeviation ? `${order.amountDeviation} %` : "-"} disabled />
            </Group>
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput label={t("common:unit")} value={order.unit?.unit ?? ""} disabled />
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              label={isSell ? t("orders:minPrice") : t("orders:maxPrice")}
              value={
                orderCurrency
                  ? isSell
                    ? (order.minPrice?.[orderCurrency]?.toString() ?? "")
                    : (order.maxPrice?.[orderCurrency]?.toString() ?? "")
                  : ""
              }
              disabled
            />
          </Grid.Col>

          {!isSell && (
            <Grid.Col span={6}>
              <TextInput label={t("dashboard:minimumAmount")} value={order.minimumAmount?.toString() ?? ""} disabled />
            </Grid.Col>
          )}
        </Grid>
      </>
    ),
  };

  const imageStep: StepConfig = {
    label: t("common:image"),
    content:
      orderImages && orderImages.length > 0 ? (
        <ImageCarousel images={orderImages} isLoading={isLoadingImages} />
      ) : (
        <Text color="secondary">{t("common:noImagesFound")}</Text>
      ),
  };

  const deliveryStep: StepConfig = {
    label: t("orders:locationAndDelivery"),
    content: (
      <Grid>
        <Grid.Col span={6}>
          <DatePickerInput label={t("common:validFrom")} value={order.validFrom ?? null} onChange={() => {}} disabled />
        </Grid.Col>
        <Grid.Col span={6}>
          <DatePickerInput label={t("common:validTo")} value={order.validTo ?? null} onChange={() => {}} disabled />
        </Grid.Col>

        {!isSell && order.lastDeliveryDate && (
          <Grid.Col span={6}>
            <DatePickerInput
              label={t("orders:lastDeliveryDate")}
              value={order.lastDeliveryDate}
              onChange={() => {}}
              disabled
            />
          </Grid.Col>
        )}

        {order.orderAddress && (
          <>
            <Grid.Col span={12}>
              <Divider />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label={t("orders:orderAddress")} value={order.orderAddress.address ?? ""} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label={t("common:country")} value={order.orderAddress.countryCodeIso2 ?? ""} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label={t("orders:postCode")} value={order.orderAddress.postCode ?? ""} disabled />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput label={t("orders:place")} value={order.orderAddress.place ?? ""} disabled />
            </Grid.Col>
          </>
        )}

        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>
        <Grid.Col span={12}>
          <CoordinateFields
            control={control}
            latitudeName="latitude"
            longitudeName="longitude"
            setValue={setValue}
            disabled={!CAN_EDIT_COORDINATES || isViewer(currentCompany)}
            onCaptured={(capturedAt) => setValue("capturedAt", capturedAt, { shouldDirty: true })}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <TextInput label={t("common:description")} value={order.description ?? ""} disabled />
        </Grid.Col>
      </Grid>
    ),
  };

  const stepConfigs: StepConfig[] = isSell ? [productStep, imageStep, deliveryStep] : [productStep, deliveryStep];

  return (
    <>
      <title>Reviver - Order Details</title>

      <div className={styles.orderDetailsRoot}>
        <div className={styles.orderDetailsInner}>
          <Group align="center">
            <SVGAsset style={{ cursor: "pointer" }} onClick={() => navigate("/market")} name="arrow-left" />
            <Text size="md" display="technical" tt="uppercase" strong={true}>
              {t("orders:activeOrders")}
            </Text>
          </Group>

          <OrderDetailsHeader order={order} isSell={isSell} />

          <form onSubmit={onSubmit}>
            <Stepper
              active={currentStep}
              onStepClick={setCurrentStep}
              classNames={{
                stepIcon: styles.stepIcon,
                separator: styles.separator,
                steps: styles.stepperHeader,
                content: styles.stepperContent,
              }}
            >
              {stepConfigs.map((step, index) => (
                <Stepper.Step key={index} label={step.label} completedIcon={index + 1}>
                  {step.content}
                </Stepper.Step>
              ))}
            </Stepper>

            <Group justify="center" className={styles.stepNavigationButtons}>
              <UnstyledButton onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)}>
                <Text size="sm" display="technical">
                  {currentStep > 0 ? `\u003C  ${stepConfigs[currentStep - 1].label}` : ""}
                </Text>
              </UnstyledButton>
              <UnstyledButton onClick={() => currentStep < stepConfigs.length - 1 && setCurrentStep(currentStep + 1)}>
                <Text size="sm" display="technical">
                  {currentStep < stepConfigs.length - 1 ? `${stepConfigs[currentStep + 1].label}  \u003E` : ""}
                </Text>
              </UnstyledButton>
            </Group>

            <footer className={styles.orderDetailsFooter}>
              <Grid>
                <Grid.Col span={12}>
                  <Group justify="flex-end">
                    <CTA id="order-details-save" type="submit" intent="primary" size="sm" loading={isPending}>
                      {t("actions:save")}
                    </CTA>
                  </Group>
                </Grid.Col>
              </Grid>
            </footer>
          </form>
        </div>
      </div>
    </>
  );
}

interface OrderDetailsHeaderProps {
  order: Order;
  isSell: boolean;
}

const OrderDetailsHeader: FC<OrderDetailsHeaderProps> = ({ order, isSell }) => {
  const { user } = useAuth();

  const currency = order.orderCurrency;
  const salesItemName =
    user?.language.id && order.salesItem.nameTranslated?.[user.language.id]
      ? order.salesItem.nameTranslated[user.language.id]
      : order.salesItem.salesItem;
  const priceText = currency && order.price ? `${order.price[currency]} ${currency} / ${order.unit?.unit ?? ""}` : "";
  const salesTypeIcon = isSell ? "salesIndicatorLarge" : "purchaseIndicatorLarge";

  return (
    <header className={styles.orderDetailsHeader}>
      <Group justify="space-between" gap="20px" align="flex-start">
        <Group justify="flex-start" align="flex-start" gap="16px">
          <SVGAsset name={salesTypeIcon} width="36" height="36" />
          <Stack gap={4}>
            <Heading order={4} styleOrder={5} strong={true}>
              {salesItemName}
            </Heading>
            <Text size="sm" display="technical">
              {priceText}
            </Text>
          </Stack>
        </Group>

        <div className={styles.orderMeta}>
          <Text size="xs" color="secondary" className={styles.dateCreated}>
            {order.created && dayjs(order.created).local().format("DD.MM.YYYY")}
          </Text>
        </div>
      </Group>
    </header>
  );
};

export function convertOrderToCreateOrder(
  order: Order,
  orderCurrency: CreateOrder["orderCurrency"],
  price: number
): CreateOrder {
  return {
    companyId: order.company.id,
    userId: order.orderCreatedBy?.id as number,
    orderType: order.orderType.id,
    amountDeviation: order.amountDeviation ?? 0,
    amount: order.amount,
    minimumAmount: order.minimumAmount,
    unit: order.unit,
    price,
    priceUnit: order.priceUnit as Unit,
    priceAdjustPercentage: null,
    maxPrice: order.maxPrice?.[order.orderCurrency] ?? null,
    minPrice: order.minPrice?.[order.orderCurrency] ?? null,
    orderCurrency,
    validTo: order.validTo,
    validFrom: order.validFrom,
    deliveryStart: order.deliveryStart ?? null,
    deliveryEnd: order.deliveryEnd ?? null,
    description: order.description ?? null,
    images: [],
    // BE classifies by the uppercase PlasticType enum name — the raw salesItem code is lowercase.
    salesItem: { id: order.salesItem.id, value: order.salesItem.salesItem.toUpperCase() },
    catchDate: order.catchDate ?? null,
    expirationDate: order.expirationDate ?? null,
    packingDate: order.packingDate ?? null,
    transportFrom: order.transportFrom ?? null,
    promoted: order.promoted,
    autoSalesOrder: false,
    orderAddress: order.orderAddress ?? null,
    ...(order.pickupCoordinates ? { pickupCoordinates: order.pickupCoordinates } : {}),
    companiesToTradeWith: order.companiesToTradeWith ?? [],
    //TODO => fix this
    packaging: {
      boxtype: order.packaging?.boxtype.id ?? 0,
      numberOfBoxes: order.packaging?.numberOfBoxes ?? 0,
      amountInEachBox: order.packaging?.amountInEachBox ?? 0,
      palletPlaces: order.packaging?.palletPlaces ?? null,
      isSealed: order.packaging?.isSealed ?? false,
    },
    lastDeliveryDate: order.lastDeliveryDate ? new Date(order.lastDeliveryDate) : null,
    externalOrderid: order.externalOrderid ?? null,
    contractId: order.contractId,
    batchNumber: order.batchNumber ?? null,
    status: order.status,
    estimatedDelivery: order.estimatedDelivery ? new Date(order.estimatedDelivery) : null,
    created: order.created,
    properties: order.properties,
  };
}
